import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { StoreConfigProvider } from "./context/StoreConfigContext";
import { AuthProvider } from "./context/AuthContext";
import { LocationProvider } from "./context/LocationContext";
import { CartProvider } from "./context/CartContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import { Loader2 } from "lucide-react";

// Lazy load pages for code splitting and better performance
const Home = lazy(() => import("./pages/Home"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Contact = lazy(() => import("./pages/Contact"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const PaymentFailure = lazy(() => import("./pages/PaymentFailure"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const Offers = lazy(() => import("./pages/Offers"));

// Loading component - Shows Header/Footer to prevent layout shift
const PageLoader = () => (
  <>
    <main className="flex-1 flex items-center justify-center py-20" style={{ backgroundColor: '#FBFAFA' }}>
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#4D3B55' }} />
        <p className="text-gray-600 font-medium">Loading page...</p>
        <div className="flex gap-1 mt-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#7F5F91', animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#7F5F91', animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#7F5F91', animationDelay: '300ms' }}></div>
        </div>
      </div>
    </main>
  </>
);

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <StoreConfigProvider>
        <AuthProvider>
          <LocationProvider>
            <CartProvider>
              <div className="App min-h-screen flex flex-col">
                <ScrollToTop />
                <Header />
                <Suspense fallback={<PageLoader />}>
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/product/:slug" element={<ProductDetail />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-confirmation/:orderId?" element={<OrderConfirmation />} />
                      <Route path="/payment-failure" element={<PaymentFailure />} />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-conditions" element={<TermsConditions />} />
                      <Route path="/shipping-policy" element={<ShippingPolicy />} />
                      <Route path="/offer-sales" element={<Offers />} />
                      {/* Catch-all route for 404 */}
                      <Route path="*" element={<PageNotFound />} />
                    </Routes>
                  </main>
                </Suspense>
                <Footer />  
                <Toaster />
              </div>
            </CartProvider>
          </LocationProvider>
        </AuthProvider>
        </StoreConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;